import { useEffect, useRef, useState } from "react";
import moment, { Moment } from "moment";

import { clearSurveysFilter } from "Redux/filtersSlice";
import {
  Button,
  Card,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Stack,
  StandardTextFieldProps,
  Typography,
} from "@mui/material";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { ClockPicker } from "@mui/x-date-pickers/ClockPicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

import Modal from "Components/Modal";
import { useAppDispatch, useAppSelector } from "Hooks/useRedux";
import _ from "lodash";

interface IProps extends StandardTextFieldProps {
  name: string;
  label: string;
  data: { [key: string]: any } | null;
  setData?: (value: any) => void;
}

const timeToMoment = (time: string) => {
  const hour = Number(time.split(":")[0]);
  const minute = Number(time.split(":")[1]);

  const initialTime = moment().utcOffset(0);
  initialTime.set({ hour, minute, second: 0, millisecond: 0 });
  initialTime.toISOString();
  initialTime.format();
  return initialTime;
};

export default function TimePicker({ name, label, size, data, setData, disabled }: IProps) {
  const mounted = useRef(false);

  const [date, setDate] = useState<Moment | null>(data && data[name] ? timeToMoment(data[name]) : timeToMoment("00:00"));
  const [view, setView] = useState<"hours" | "minutes">("hours");
  const [showModal, setShowModal] = useState(false);
  const { surveys } = useAppSelector((state) => state.filters);
  const dispatch = useAppDispatch();

  const toggleModal = () => setShowModal((prev) => !prev);

  useEffect(() => {
    if (mounted.current) {
      if (setData) {
        setData(date?.format("HH:mm"));
      }
    } else {
      mounted.current = true;
    }
  }, [date]);

  const handleDeleteTime = () => {
    const newSurveys = { ...surveys };
    delete newSurveys[name];
    setDate(null);
    dispatch(clearSurveysFilter(newSurveys));
  };

  return (
    <>
      <Modal open={showModal} onClose={toggleModal} size="xs">
        <Card sx={{ p: 3 }}>
          <Stack direction="row-reverse" justifyContent="center" alignItems="center" spacing={1} mb={1}>
            {date
              ?.format("HH:mm")
              .split(":")
              .map((i, index) => {
                const value = index === 0 ? "hours" : "minutes";

                return (
                  <>
                    <Button
                      sx={{ fontSize: "20px", bgcolor: view === value ? "#eee" : "" }}
                      color="black"
                      className="fa-number"
                      onClick={() => setView(value)}
                    >
                      {i}
                    </Button>
                    {index === 0 && <Typography>:</Typography>}
                  </>
                );
              })}
          </Stack>

          <LocalizationProvider dateAdapter={AdapterMoment}>
            <ClockPicker date={date} onChange={(newDate) => setDate(newDate)} view={view} />
          </LocalizationProvider>
          <Stack direction="row" style={{ width: "100%", justifyContent: "space-between" }}>
            <Button variant="contained" size="large" sx={{ mt: 3, width: "45%" }} onClick={toggleModal}>
              تایید
            </Button>
            <Button variant="contained" size="large" sx={{ mt: 3, width: "45%" }} onClick={handleDeleteTime}>
              بازنشانی
            </Button>
          </Stack>
        </Card>
      </Modal>
      <FormControl fullWidth variant="outlined" size={size} onClick={() => setShowModal(true)}>
        <InputLabel shrink={Boolean(data && data[name])}>{label}</InputLabel>
        <OutlinedInput
          notched={Boolean(data && data[name])}
          type="text"
          label={label}
          inputProps={{ readOnly: true, className: "fa-number" }}
          disabled={disabled}
          value={!_.isEmpty(data) && data[name] ? data[name] : ""}
          required={true}
          endAdornment={
            <InputAdornment position="end">
              <IconButton edge="end" onClick={() => setShowModal(true)} disabled={disabled}>
                <AccessTimeIcon fontSize={size} />
              </IconButton>
            </InputAdornment>
          }
        />
      </FormControl>
    </>
  );
}
